import React, { useEffect, useState, createRef, useContext } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Alert,
  SubNavigation,
  BackButton,
  ActionButton,
  LoadingSpinner,
} from 'wg-fe-ui';
import { FlagsProvider } from '../../hoc/FlagsProviderHoc';
import cogoToast from 'cogo-toast';
import Frame from '../../../assets/images/Frame.svg';
import { cloneDeep, isEqual } from 'lodash';
import { getMeData } from '../../services/meDataService';
import { getBrokerData } from '../../services/brokerDataService';
import {
  getRiskObjectByTypeAndId,
  createRiskObjectRevision,
} from '../../services/apiRouterService';
import TeacherInformation from '../../components/CRMRiskObjectDetailTeacher';

const CRMRiskObjectDetailTeacher = () => {
  const configCatClient = useContext(FlagsProvider);
  const location = useLocation();
  const { t } = useTranslation();
  const history = useHistory();
  const refs = {};
  const [isLoading, setIsLoading] = useState(true);
  const [updatedTeacherData, setUpdatedTeacherData] = useState(
    location?.state?.updatedTeacherData ?? {},
  );
  const [canEditOffers, setCanEditOffers] = useState(true);
  const [updateRequired, setUpdateRequired] = useState(false);
  const [teacherData, setTeacherData] = useState(
    location?.state?.updatedTeacherData ?? {},
  );
  const [currentSection, setCurrentSection] = useState({
    section: 1,
    subsection: 1,
  });
  const {
    id: riskObjectId,
    revision: riskObjectRevision,
    offerId,
    contractCaseId,
    contractId,
    prospectId,
    leadId,
    offerRevision,
    riskObjectType,
    editing,
  } = useParams();

  useEffect(() => {
    setFeatureToggles();
    if (location?.state?.updatedTeacherData) return setIsLoading(false);
    retrieveTeacherData();
  }, []);

  async function setFeatureToggles() {
    const { me } = (await getMeData()) || {};
    const { broker } = (await getBrokerData()) || {};
    if (offerId || contractId || prospectId) {
      setCanEditOffers(
        leadId
          ? false
          : await configCatClient.getValueAsync(
              offerId || contractId
                ? 'editOffers'
                : 'canEditProspectsAndRiskObjects',
              false,
              {
                email: me?.email,
                custom: {
                  plan: broker?.plan,
                },
              },
            ),
      );
    }
  }

  async function retrieveTeacherData() {
    const { distribution_id } = await getBrokerData();
    const [response, status] = await getRiskObjectByTypeAndId(
      distribution_id,
      'teachers',
      riskObjectId,
      riskObjectRevision,
    );

    if (status !== 200) {
      if (status !== 404) {
        return console.error(response);
      }
      console.log('FAILED TO LOAD');
      setTeacherData(null);
      return;
    }
    setTeacherData(response);
    console.log(response);
    setUpdatedTeacherData(response);
    setIsLoading(false);
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  });

  const showUpdateMessage = () => {
    setUpdateRequired(true);
  };

  const teacherInformationSave = values => {
    const payload = cloneDeep(values);
    payload.risk_object_id = updatedTeacherData.risk_object_id;
    payload.risk_object_revision_id =
      updatedTeacherData.risk_object_revision_id;

    setUpdatedTeacherData(payload);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    if (!isEqual(teacherData, payload)) {
      showUpdateMessage();
    }
  };

  const [sections] = useState([
    {
      id: 1,
      label: `${t('Risk object')} - ${t('Teacher')}`,
      subsections: [
        {
          id: 1,
          label: t('Teacher information'),
        },
      ],
    },
  ]);

  sections.forEach(s => {
    refs[s.id] = {};

    s.subsections.forEach(value => {
      refs[s.id][value.id] = createRef();
    });
  });

  const handleSideBarClick = (section, sub) => {
    const yCoordinate =
      refs[section][sub].current.getBoundingClientRect().top +
      window.pageYOffset;

    window.scrollTo({
      top: yCoordinate - 160,
      behavior: 'smooth',
    });
  };

  const handleSaveTeacherOfferChanges = () => {
    const pathname =
      prospectId && offerId
        ? `/sales-management/customer/${prospectId}/contracts/offer/${offerId}/${offerRevision}`
        : `/sales-management/offer/${offerId}/${offerRevision}`;
    history.push({
      pathname,
      state: { ...location.state, updatedTeacherData },
    });
  };

  const handleSaveTeacherContractChanges = () => {
    const pathname = prospectId
      ? `/sales-management/customer/${prospectId}/contracts/case/${contractCaseId}/contract/${contractId}`
      : `/sales-management/case/${contractCaseId}/contract/${contractId}`;
    history.push({
      pathname,
      state: { ...location.state, updatedTeacherData },
    });
  };

  const handleSaveTeacherChanges = async () => {
    const { distribution_id } = await getBrokerData();
    const tempUpdatedTeacherData = cloneDeep(updatedTeacherData);
    delete tempUpdatedTeacherData.id;
    delete tempUpdatedTeacherData.risk_object_id;
    delete tempUpdatedTeacherData.risk_object_revision_id;
    let [response, status] = await createRiskObjectRevision(
      distribution_id,
      leadId || prospectId,
      'teachers',
      riskObjectId,
      tempUpdatedTeacherData,
    );

    if (!(status < 204 && status >= 200)) {
      cogoToast.error(t('Something went wrong'));
      return console.error(response);
    }

    cogoToast.success(t('Risk object revision successfully saved'));

    setUpdateRequired(false);
    history.push({
      pathname:
        prospectId && leadId
          ? `/sales-management/customer/${prospectId}/contracts/lead/${leadId}/risk-object/${riskObjectType}/${riskObjectId}/${response.risk_object_revision_id}`
          : `/sales-management/customer/${prospectId}/risk-object/${riskObjectType}/${riskObjectId}/${response.risk_object_revision_id}`,
      state: { ...location.state },
    });
  };

  const handleScroll = () => {
    const currentScroll = window.pageYOffset;
    // Iterate over sections refs
    for (const [key, val] of Object.entries(refs)) {
      // Iterate over subsections refs
      for (const [k, v] of Object.entries(val)) {
        if (v.current && currentSection !== k) {
          const currentElScroll =
            v.current.getBoundingClientRect().top + currentScroll;
          if (currentElScroll > currentScroll) {
            setCurrentSection({ section: Number(key), subsection: Number(k) });
            return;
          }
        }
      }
    }
  };

  const backButtonHandler = () => {
    let pathname = '';
    if (prospectId && offerId) {
      pathname = `/sales-management/customer/${prospectId}/contracts/offer/${offerId}/${offerRevision}`;
    } else if (prospectId && contractId) {
      pathname = `/sales-management/customer/${prospectId}/contracts/case/${contractCaseId}/contract/${contractId}`;
    } else if (prospectId && leadId) {
      pathname = `/sales-management/customer/${prospectId}/contracts/lead/${leadId}`;
    } else if (prospectId) {
      pathname = `/sales-management/customer/${prospectId}`;
    } else if (contractId) {
      pathname = `/sales-management/case/${contractCaseId}/contract/${contractId}`;
    } else if (leadId) {
      pathname = `/sales-management/lead/${leadId}`;
    } else {
      pathname = `/sales-management/offer/${offerId}/${offerRevision}`;
    }
    history.push({
      pathname,
      state: { ...location.state },
    });
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (teacherData == null) {
    return (
      <EmptyState>
        <img src={Frame} alt="Test" />
        <h1>{t('Risk object not found')}</h1>
        <p>
          {`${t('We could not find the risk object with id')}: `}{' '}
          <strong>{riskObjectId}</strong>
        </p>
      </EmptyState>
    );
  }

  return (
    <MainContainer>
      <TopBar>
        <TopBarContainer>
          <TopBarLeft>
            <BackButton onClick={backButtonHandler} name={t('Previous page')} />
          </TopBarLeft>
        </TopBarContainer>
      </TopBar>

      <MainContent>
        <Sidebar>
          {sections.map(section => (
            <SubNavigation key={section.id}>
              <SubNavigation.Section>{section.label}</SubNavigation.Section>
              <SubNavigation.SectionContainer
                active={section.id === currentSection.section}
              >
                {section.subsections.map(subsection => (
                  <SubNavigation.SubSection
                    style={{ width: '20rem' }}
                    key={subsection.id}
                    onClick={() =>
                      handleSideBarClick(section.id, subsection.id)
                    }
                    active={
                      currentSection.section === section.id &&
                      currentSection.subsection === subsection.id
                    }
                  >
                    {subsection.label}
                  </SubNavigation.SubSection>
                ))}
              </SubNavigation.SectionContainer>
            </SubNavigation>
          ))}
        </Sidebar>

        <Content>
          {updateRequired && offerId && !contractId ? (
            <StyledAlert icon type="normal">
              <StyledAlertContent>
                {t(
                  'You have made changes to the risk object, would you like to save these changes and go back to the offer',
                )}
                <ActionButton onClick={() => handleSaveTeacherOfferChanges()}>
                  {t(`Save`)}
                </ActionButton>
              </StyledAlertContent>
            </StyledAlert>
          ) : null}
          {updateRequired && contractId ? (
            <StyledAlert icon type="normal">
              <StyledAlertContent>
                {t(
                  'You have made changes to the risk object, would you like to save these changes and go back to the contract',
                )}
                <ActionButton
                  onClick={() => handleSaveTeacherContractChanges()}
                >
                  {t(`Save`)}
                </ActionButton>
              </StyledAlertContent>
            </StyledAlert>
          ) : null}
          {updateRequired &&
          (prospectId || leadId) &&
          !contractId &&
          !offerId ? (
            <StyledAlert icon type="normal">
              <StyledAlertContent>
                {t(
                  'You have made changes to the risk object would you like to save these changes',
                )}
                <ActionButton onClick={() => handleSaveTeacherChanges()}>
                  {t(`Save`)}
                </ActionButton>
              </StyledAlertContent>
            </StyledAlert>
          ) : null}
          <section ref={refs[1][1]}>
            {/* <Section>
              <Section.Title>{t('Teacher information')}</Section.Title>
              <Section.Content background>
                <SectionMain>
                  <SectionMainItem>
                    <Text>
                      {teacherData?.manipulates_heavy_machinery
                        ? t('Does operate heavy machinery')
                        : t('Does not operate heavy machinery')}
                    </Text>
                  </SectionMainItem>
                </SectionMain>
              </Section.Content>
            </Section> */}

            <TeacherInformation
              teacherData={updatedTeacherData}
              initEdit={editing ?? false}
              disableEditing={!canEditOffers}
              onSubmitFunction={teacherInformationValues =>
                teacherInformationSave(teacherInformationValues)
              }
            ></TeacherInformation>
          </section>
        </Content>
      </MainContent>
    </MainContainer>
  );
};

const EmptyState = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  img {
    margin-left: 55px;
    margin-bottom: 50px;
  }

  h1 {
    font-family: ${({ theme }) => theme.font};
    font-weight: bold;
    font-size: 2.1rem;
    line-height: 135%;
    text-align: center;
    letter-spacing: -0.03em;
    color: black;
    margin-bottom: 10px;
  }

  p {
    font-family: ${({ theme }) => theme.font};
    font-size: 1.6rem;
    line-height: 135%;
    text-align: center;
    color: black;

    strong {
      font-weight: bold;
    }
  }
`;

const MainContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.div`
  width: 100%;
  background-color: #f7f7f7;
  position: sticky;
  top: 8rem;
  z-index: 10;
  border-bottom: 1px solid #dbdbdb;

  @media screen and (max-width: 1240px) {
    top: 8rem;
  }
`;

const TopBarContainer = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  align-items: center;
  display: flex;
  padding: 12px 30px;
`;

const TopBarLeft = styled.div`
  flex: 0 0 220px;
`;

const MainContent = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  padding: 55px 30px;
  position: relative;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  position: sticky;
  top: 200px;
  margin-right: 32px;
  flex: 0 0 220px;

  & > button {
    margin-right: 0.78vw;
  }

  @media screen and (max-width: 1500px) {
    flex: 0 0 150px;
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;

  > div {
    width: 100%;
  }
`;

const StyledAlertContent = styled.div`
  font-size: 1.4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: auto;
`;

const LoadingContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Content = styled.div`
  flex: 0 1 auto;
  max-width: 100rem;
  width: 100%;
`;

export default CRMRiskObjectDetailTeacher;
